import type { ErrorEvent } from '@sentry/nuxt'
import type { ApiUser } from '../types'

/**
 * Minimal event shape accepted by the scrubber.
 * Runtime Sentry events are full ErrorEvent objects; tests use partial fixtures.
 */
export type SentryEvent = Partial<ErrorEvent> & {
    type?: undefined
}

/**
 * Breadcrumb categories that should have their message and data stripped
 * to prevent capturing sensitive request/response information.
 */
const BREADCRUMB_CATEGORIES_TO_STRIP: Record<string, true> = {
    'http.client': true,
    'http.server': true,
    console: true,
    xhr: true,
}

/**
 * Scrub sensitive data from a Sentry event.
 * Removes headers, cookies, query strings, request bodies, and sensitive breadcrumb data.
 * Removes PII fields from user context except id and username.
 *
 * @param event - The Sentry event to scrub
 * @returns The scrubbed event
 */
export function scrubSensitiveData<T extends SentryEvent>(event: T): T {
    const scrubbed: SentryEvent = { ...event }

    // Scrub request data
    if (scrubbed.request) {
        scrubbed.request = { ...scrubbed.request }

        // Remove ALL headers
        delete scrubbed.request.headers

        // Remove all cookies
        delete scrubbed.request.cookies

        // Remove query string and strip query from URL
        if (scrubbed.request.url) {
            try {
                const url = new URL(scrubbed.request.url)
                scrubbed.request.url = url.origin + url.pathname
            } catch {
                // If URL parsing fails, attempt simple string stripping
                scrubbed.request.url = scrubbed.request.url.split('?')[0]
            }
        }
        // Remove query_string field
        delete scrubbed.request.query_string

        // Remove request body/data unconditionally (prevents falsy-but-sensitive values from surviving)
        delete scrubbed.request.data
    }

    // Scrub breadcrumbs: remove message and data for sensitive categories
    if (scrubbed.breadcrumbs && Array.isArray(scrubbed.breadcrumbs)) {
        scrubbed.breadcrumbs = scrubbed.breadcrumbs.map((breadcrumb) => {
            if (breadcrumb.category && BREADCRUMB_CATEGORIES_TO_STRIP[breadcrumb.category]) {
                const { message, data, ...safe } = breadcrumb
                return safe
            }
            return breadcrumb
        })
    }

    // Scrub user PII: keep only id and username
    if (scrubbed.user) {
        const { id, username } = scrubbed.user
        scrubbed.user = {}
        if (id !== undefined) {
            scrubbed.user.id = id
        }
        if (username !== undefined) {
            scrubbed.user.username = username
        }
    }

    return scrubbed as T
}

/**
 * Project an ApiUser to Sentry user context.
 * Only allows id and username to be sent to Sentry.
 * Strips email, IP, and all other PII.
 *
 * @param user - The API user object
 * @returns A projection with only { id, username } or null
 */
export function projectUserContext(
    user: ApiUser | null | undefined,
): { id: string; username: string } | null {
    if (!user) {
        return null
    }

    return {
        id: String(user.id),
        username: user.name,
    }
}
